import type { PollAuthResult } from "../../../api/copilotAgentTypes";

export const modelsFallback = [
  { id: "gpt-4.1", label: "GPT-4.1" },
  { id: "gpt-4o", label: "GPT-4o" },
  { id: "claude-sonnet-4.5", label: "Claude Sonnet 4.5" },
  { id: "o3", label: "o3" },
];

export const toolDisplayNames: Record<string, string> = {
  app_insights: "App Insights",
  mongodb: "MongoDB",
  app_config: "App Config",
  blob_storage: "Blob Storage",
  azure_resources: "Azure Resources",
  intercom: "Intercom",
  intercom_write: "Intercom (Write)",
  mailgun: "Mailgun",
  github_code: "GitHub Code",
  github_code_accounting: "GitHub Code (Accounting)",
  github_project: "GitHub Project",
  github_issue_write: "GitHub Issue (Write)",
  snowflake: "Snowflake (BC Data)",
  business_central: "Business Central",
  clients: "Clients",
  feature_toggles: "Feature Flags",
  feature_toggle_write: "Feature Flags (Write)",
};

const effortLabels: Record<string, string> = {
  none: "None",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra High",
  max: "Max",
};

export const formatEffortLabel = (effort: string): string =>
  effortLabels[effort.toLowerCase()] ??
  effort
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const parseVersion = (s: string): number[] => {
  const match = s.match(/(\d+(?:\.\d+)*)/);
  if (!match) {
    return [];
  }
  return (match[1] ?? "").split(".").map(Number);
};

const compareVersions = (a: number[], b: number[]): number => {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }
  return 0;
};

export const pickBestClaudeSonnet = (
  models: { id: string; label: string }[]
): { id: string; label: string } | undefined => {
  const sonnets = models.filter((m) => m.id.toLowerCase().includes("claude") && m.id.toLowerCase().includes("sonnet"));
  if (sonnets.length === 0) {
    return undefined;
  }
  return sonnets.sort((a, b) => compareVersions(parseVersion(b.id), parseVersion(a.id)))[0];
};

export const mapPollAuthError = (
  error?: string | null,
  gitHubLogin?: string | null
): Omit<PollAuthResult, "authorized"> => {
  switch (error) {
    case undefined:
    case null:
    case "authorization_pending":
      return { continuePolling: true };
    case "slow_down":
      return { continuePolling: true, message: "GitHub asked to slow down. Retrying automatically." };
    case "access_denied":
      return { continuePolling: false, message: "GitHub authorization was denied. Start again to retry." };
    case "expired_token":
      return { continuePolling: false, message: "GitHub authorization expired. Start again to retry." };
    case "request_failed":
      return { continuePolling: false, message: "GitHub authorization check failed. Start again to retry." };
    case "no_repo_access": {
      const account = gitHubLogin ? `Your GitHub account ${gitHubLogin}` : "Your GitHub account";
      return {
        continuePolling: false,
        message:
          `${account} can't access the entrilia-web-services repository. ` +
          "Sign in with your work GitHub account, or ask an admin to check its access to the repository. Then start again.",
      };
    }
    default:
      return { continuePolling: false, message: `GitHub authorization failed: ${error}` };
  }
};

export const resolveEffort = (
  info: { supported: string[]; default: string | null } | undefined,
  preferred?: string | null
): string | undefined => {
  const supported = info?.supported ?? [];
  if (supported.length === 0) {
    return undefined;
  }
  if (preferred && supported.includes(preferred)) {
    return preferred;
  }
  if (info?.default && supported.includes(info.default)) {
    return info.default;
  }
  return supported[supported.length - 1];
};

export const formatReasoning = (text: string): string => text.replace(/\.(?=[A-Z])/g, ".\n");
